import { useEffect, useState } from "react";
import { UserCheck, ArrowRightLeft, StickyNote, Download, X, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function BulkActionBar({ selectedIds, users, onDone, onClear }) {
  const { user, options } = useAuth();
  const [dialog, setDialog] = useState(null); // 'assign' | 'stage' | 'note'
  const [owner, setOwner] = useState("");
  const [stage, setStage] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [safeStages, setSafeStages] = useState([]);
  const [delOpen, setDelOpen] = useState(false);

  const isManager = user?.role !== "Sales Executive";
  const isAdmin = user?.role === "Admin";
  const count = selectedIds.length;

  useEffect(() => {
    if (!isManager) return;
    api.get("/automation/config").then(({ data }) => setSafeStages(data.data.bulk_safe_stages || [])).catch(() => {});
  }, [isManager]);

  const reset = () => { setDialog(null); setOwner(""); setStage(""); setNote(""); };

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(`${successMsg} · ${data.data.updated} lead(s)`);
      reset();
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doAssign = () => {
    if (!owner) return toast.error("Pick an owner");
    run(() => api.post("/leads/bulk/assign", { lead_ids: selectedIds, owner_user_id: owner }), "Reassigned");
  };
  const doStage = () => {
    if (!stage) return toast.error("Pick a stage");
    run(() => api.post("/leads/bulk/set-stage", { lead_ids: selectedIds, stage }), "Stage updated");
  };
  const doNote = () => {
    if (!note.trim()) return toast.error("Enter a note");
    run(() => api.post("/leads/bulk/add-note", { lead_ids: selectedIds, note }), "Note added to");
  };
  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.post("/leads/bulk/export", { lead_ids: selectedIds }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "acquireloop_leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count} lead(s)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    const { data } = await api.post("/leads/bulk/delete", { lead_ids: selectedIds });
    toast.success(`${data.data.deleted} lead(s) moved to recycle bin`);
    onDone();
  };

  if (count === 0) return null;

  return (
    <>
      <div data-testid="bulk-action-bar" className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-2xl border border-sky bg-white px-3 py-2.5 shadow-2xl shadow-navy/20">
        <span className="flex items-center gap-2 rounded-xl bg-navy px-3 py-1.5 text-sm font-semibold text-white" data-testid="bulk-count">
          {count} selected
        </span>
        {isManager && (
          <>
            <button onClick={() => setDialog("assign")} data-testid="bulk-assign-btn" className="flex items-center gap-1.5 rounded-xl border border-sky px-3 py-2 text-sm font-medium text-navy hover:border-loop hover:text-loop"><UserCheck className="h-4 w-4" />Assign</button>
            <button onClick={() => setDialog("stage")} data-testid="bulk-stage-btn" className="flex items-center gap-1.5 rounded-xl border border-sky px-3 py-2 text-sm font-medium text-navy hover:border-loop hover:text-loop"><ArrowRightLeft className="h-4 w-4" />Stage</button>
          </>
        )}
        <button onClick={() => setDialog("note")} data-testid="bulk-note-btn" className="flex items-center gap-1.5 rounded-xl border border-sky px-3 py-2 text-sm font-medium text-navy hover:border-loop hover:text-loop"><StickyNote className="h-4 w-4" />Note</button>
        {isAdmin && (
          <button onClick={doExport} disabled={busy} data-testid="bulk-export-btn" className="flex items-center gap-1.5 rounded-xl border border-sky px-3 py-2 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export</button>
        )}
        {isAdmin && (
          <button onClick={() => setDelOpen(true)} disabled={busy} data-testid="bulk-delete-btn" className="flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-60"><Trash2 className="h-4 w-4" />Delete</button>
        )}
        <button onClick={onClear} data-testid="bulk-clear-btn" className="rounded-xl border border-sky p-2 text-ink/50 hover:bg-mist"><X className="h-4 w-4" /></button>
      </div>

      <ConfirmDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        title={`Delete ${count} lead(s)?`}
        description="Selected leads will be moved to the recycle bin (soft-deleted). An Admin can restore them later."
        confirmLabel="Delete"
        testid="bulk-delete-confirm"
        onConfirm={doDelete}
      />

      {/* Assign */}
      <Dialog open={dialog === "assign"} onOpenChange={(v) => !v && reset()}>
        <DialogContent className="border-sky bg-white sm:max-w-sm" data-testid="bulk-assign-dialog">
          <DialogHeader><DialogTitle className="font-sora text-navy">Reassign {count} lead(s)</DialogTitle>
            <DialogDescription>Pick the new owner. This sets ownership manually.</DialogDescription></DialogHeader>
          <select data-testid="bulk-assign-select" value={owner} onChange={(e) => setOwner(e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop">
            <option value="">Select owner…</option>
            {users?.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
          </select>
          <button onClick={doAssign} disabled={busy} data-testid="bulk-assign-confirm" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reassign"}</button>
        </DialogContent>
      </Dialog>

      {/* Change stage */}
      <Dialog open={dialog === "stage"} onOpenChange={(v) => !v && reset()}>
        <DialogContent className="border-sky bg-white sm:max-w-sm" data-testid="bulk-stage-dialog">
          <DialogHeader><DialogTitle className="font-sora text-navy">Change stage for {count} lead(s)</DialogTitle>
            <DialogDescription>Only stages with no per-lead requirements can be set in bulk. Stages needing qualification, an activity, or a reason must be moved individually.</DialogDescription></DialogHeader>
          <select data-testid="bulk-stage-select" value={stage} onChange={(e) => setStage(e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3 text-sm outline-none focus:border-loop">
            <option value="">Select stage…</option>
            {options?.stages?.map((s) => {
              const safe = safeStages.includes(s);
              const name = options?.stage_meta?.[s]?.name || s;
              return <option key={s} value={s} disabled={!safe}>{name}{safe ? "" : " — move individually"}</option>;
            })}
          </select>
          <button onClick={doStage} disabled={busy} data-testid="bulk-stage-confirm" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update stage"}</button>
        </DialogContent>
      </Dialog>

      {/* Add note */}
      <Dialog open={dialog === "note"} onOpenChange={(v) => !v && reset()}>
        <DialogContent className="border-sky bg-white sm:max-w-md" data-testid="bulk-note-dialog">
          <DialogHeader><DialogTitle className="font-sora text-navy">Add note to {count} lead(s)</DialogTitle>
            <DialogDescription>The note is appended to each lead's internal notes and logged on their timeline.</DialogDescription></DialogHeader>
          <textarea data-testid="bulk-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Type a note to add to all selected leads…" className="w-full rounded-xl border border-sky p-3 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
          <button onClick={doNote} disabled={busy} data-testid="bulk-note-confirm" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}</button>
        </DialogContent>
      </Dialog>
    </>
  );
}
